import React, {
  createContext,
  ReactNode,
  useContext,
  useState,
} from "react";
import { useMutation } from '@tanstack/react-query'
import { CadastroUsuarioRequest, Credor, Perfil, Usuario, UsuarioAtualizar } from "../../types/supervisao/usuario-interface";
import { useModal } from "../../hooks/ModalContext";
import { apiModulo } from '../../services/api'


interface UsuarioContextProviderProps {
  children: ReactNode;
}

interface UsuarioContextProps {
  usuarios: Usuario[]
  perfils: Perfil[]
  credores: Credor[]
  getUsuario(filter?: number): Promise<void>
  getPerfils(): Promise<void>
  getCredores(): Promise<void>
  cadastrarUsuario(data: CadastroUsuarioRequest): Promise<void>
  atualizarCadastro(data: UsuarioAtualizar): Promise<void>
}

const UsuarioContext = createContext<UsuarioContextProps>({} as UsuarioContextProps);

export const UsuarioProvider = ({ children }: UsuarioContextProviderProps) => {
  const { setModal } = useModal()

  const [usuarios, setUsuarios] = useState<Usuario[]>([] as Usuario[])
  const [perfils, setPerfils] = useState<Perfil[]>([] as Perfil[])
  const [credores, setCredores] = useState<Credor[]>([] as Credor[])

  const mCadastroUsuario = useMutation({
    mutationFn: (data: CadastroUsuarioRequest) => apiModulo.post('user', { ...data }),
    mutationKey: ['usuario'],
    onError: () => setModal('Ops! Ocorreu um erro em nosso sistema. Tente novamente mais tarde!', 'Erro', false, true),
    onSuccess: () => setModal('Usuário cadastrado com sucesso!', 'Sucesso', true, false),
  })
  const mGetUsuario = useMutation({
    mutationFn: ({ filter }: { filter?: number }) => apiModulo.get('users', { params: { filter: filter || '' } }),
    mutationKey: ['usuario'],
    onError: () => setModal('Ops! Ocorreu um erro em nosso sistema. Tente novamente mais tarde!', 'Erro', true, false)
  })
  const mGetPerils = useMutation({
    mutationFn: () => apiModulo.get('perfils'),
    mutationKey: ['usuario'],
    onError: () => setModal('Ops! Ocorreu um erro em nosso sistema. Tente novamente mais tarde!', 'Erro', false, true)
  })

  const mGetCredor = useMutation({
    mutationFn: () => apiModulo.get('credor'),
    mutationKey: ['usuario'],
    onError: () => setModal('Ops! Ocorreu um erro em nosso sistema. Tente novamente mais tarde!', 'Erro', false, true)
  })

  const mAtualizarUsuario = useMutation({
    mutationFn: (data: UsuarioAtualizar) => apiModulo.put('user', { ...data }),
    mutationKey: ['usuario'],
    onError: () => setModal('Ops! Ocorreu um erro em nosso sistema. Tente novamente mais tarde!', 'Erro', false, true),
    onSuccess: () => {
      setModal('Usuário atualizado com sucesso!', 'Sucesso', true, false)
      getUsuario()
    },
  })

  async function atualizarCadastro(data: UsuarioAtualizar) {
    await mAtualizarUsuario.mutateAsync(data)
  }

  async function getUsuario(filter?: number) {
    const { data } = await mGetUsuario.mutateAsync({ filter })
    setUsuarios(data.users)
  }

  async function getPerfils() {
    const { data } = await mGetPerils.mutateAsync()
    setPerfils(data.perfils)
  }

  async function getCredores() {
    const { data } = await mGetCredor.mutateAsync()
    setCredores(data.credores)
  }

  async function cadastrarUsuario(data: CadastroUsuarioRequest) {
    await mCadastroUsuario.mutateAsync(data)
  }


  return (
    <UsuarioContext.Provider value={{
      credores,
      perfils,
      usuarios,
      cadastrarUsuario,
      getCredores,
      getPerfils,
      getUsuario,
      atualizarCadastro
    }}>
      {children}
    </UsuarioContext.Provider>
  );
}

export const useUsuario = () => useContext(UsuarioContext);